/**
 * MpReview.tsx —— 对局结束后的复盘（赢率解禁区）。
 *
 * 门控红线：本组件只在本手结束（handComplete/gameOver）后被打开。此处展示的赢率与单机口径一致：
 *   「你 vs 1 名随机对手」的**精确**值（翻牌前查表、翻牌后全量枚举），无采样、无估算。
 * 计算完全在客户端本地进行（基于服务端下发的：你自己的底牌 + 逐街公共牌），不经服务端。
 */

import { useEffect, useMemo, useState } from 'react';
import type { HandReview } from '../../net/protocol';
import { calculateEquity, headsUpEquity, type EquityResult } from '../../core/equity';
import { pct1, STREET_CN, chips } from '../../state/format';
import { setLetwayFloat } from '../../state/letway';
import { PlayingCard } from '../primitives';
import { runMultiway } from '../../state/multiwayClient';
import type { MultiwayRowOutput } from '../../state/multiwayTypes';

/**
 * 逐街快照去重（BUG-R15-03）：服务端预置了一条 preflop 快照，事件流里还会再推一条同名街，
 * 导致复盘表「翻牌前」重复两行。这里按 street 去重（保留首次出现，公共牌张数取更完整的一条）。
 */
export function dedupeStreets(streets: HandReview['streets']): HandReview['streets'] {
  const out: { street: HandReview['streets'][number]['street']; board: readonly number[] }[] = [];
  for (const snap of streets) {
    const hit = out.find((x) => x.street === snap.street);
    if (!hit) { out.push({ street: snap.street, board: snap.board }); continue; }
    if (snap.board.length > hit.board.length) hit.board = snap.board;
  }
  return out;
}

/**
 * 复盘赢率口径（PRD 4.4「根据已知的各方底牌」）：
 *   - 摊牌为 1v1（你 + 1 名亮牌对手）→ 用**对手真实底牌**做全量枚举精算；
 *   - 摊牌为多路（你 + ≥2 名亮牌对手）→ 用**全部真实底牌**做多路全量枚举精算（第 18 轮 L1），
 *     平分底池按并列人数折算，无近似路径；
 *   - 其余情形（未摊牌 / 你未参与摊牌）→ 回落到「你 vs 1 名随机对手」，文案显式标注口径与真实对手数。
 */
export function pickCalibration(review: HandReview): {
  mode: 'realHoles' | 'multiway' | 'vsRandom';
  villain: readonly number[] | null;
  villains: readonly (readonly number[])[];
  text: string;
} {
  const reveals = review.reveals ?? [];
  const villains = reveals.filter((r) => r.seat !== review.yourSeat);
  const heroRevealed = !!review.yourHole && reveals.some((r) => r.seat === review.yourSeat);
  if (heroRevealed && villains.length === 1) {
    return {
      mode: 'realHoles',
      villain: villains[0].hole,
      villains: [villains[0].hole],
      text: `口径：你 vs ${villains[0].name} 的真实底牌（摊牌已公开），逐街全量枚举精算，无采样。`,
    };
  }
  if (heroRevealed && villains.length >= 2) {
    // 遗留项 L1：多路真实底牌全量枚举精算（无近似路径）
    return {
      mode: 'multiway',
      villain: null,
      villains: villains.map((v) => v.hole),
      text: `口径：你 vs ${villains.map((v) => v.name).join(' / ')} 的真实底牌（${villains.length} 名对手摊牌已公开），`
        + '逐街全量枚举的多路精算，无采样、无近似；平分底池按并列人数折算。',
    };
  }
  const opponents = Math.max(0, review.net.length - 1);
  return {
    mode: 'vsRandom',
    villain: null,
    villains: [],
    text: `口径：本手未获得全部对手的真实底牌${villains.length > 1 ? '（多人摊牌，但你未参与摊牌）' : '（对手未摊牌）'}，`
      + `因此展示「你 vs 1 名随机对手」的精确值（全量枚举，无采样）。本桌实际对手数为 ${opponents} 名，该数值不随对手数变化。`,
  };
}

interface EquityRow {
  street: string;
  board: readonly number[];
  result: EquityResult | null;
  equity?: number;
  exact?: boolean;
  pending?: boolean;
  err?: string;
}

export function MpReview({ review, onClose }: { review: HandReview; onClose: () => void }) {
  // 二期 FR-E2：复盘抽屉在场登记为 mp-review 查阅面板 → 触发 pwa 让位（AC-E2 矩阵 review 行）。
  useEffect(() => {
    setLetwayFloat('mp-review', true);
    return () => setLetwayFloat('mp-review', false);
  }, []);
  const calib = useMemo(() => pickCalibration(review), [review]);
  const streets = useMemo(() => dedupeStreets(review.streets), [review]);
  /** 多路精算结果（key = 街名）；多路场景下走 Worker 异步计算，避免阻塞主线程。 */
  const [multiway, setMultiway] = useState<Record<string, MultiwayRowOutput> | null>(null);

  useEffect(() => {
    setMultiway(null);
    if (calib.mode !== 'multiway' || !review.yourHole) return;
    const rows = streets.map((s) => ({
      key: s.street as string,
      hero: review.yourHole as readonly number[],
      villains: calib.villains,
      board: s.board,
    }));
    return runMultiway(rows, (out) => {
      const map: Record<string, MultiwayRowOutput> = {};
      for (const r of out) map[r.key] = r;
      setMultiway(map);
    });
  }, [review, calib, streets]);

  const equityRows = useMemo<EquityRow[]>(() => {
    if (!review.yourHole) return [];
    const rows: EquityRow[] = [];
    for (const snap of streets) {
      try {
        if (calib.mode === 'multiway') {
          const hit = multiway?.[snap.street];
          if (!hit) { rows.push({ street: STREET_CN[snap.street], board: snap.board, result: null, pending: true }); continue; }
          if (hit.error) { rows.push({ street: STREET_CN[snap.street], board: snap.board, result: null, err: hit.error }); continue; }
          rows.push({ street: STREET_CN[snap.street], board: snap.board, result: null, equity: hit.equity, exact: hit.exact });
        } else if (calib.mode === 'realHoles' && calib.villain) {
          const r = headsUpEquity(review.yourHole, calib.villain, snap.board);
          rows.push({ street: STREET_CN[snap.street], board: snap.board, result: null, equity: r.equity, exact: true });
        } else {
          const r = calculateEquity(review.yourHole, snap.board);
          rows.push({ street: STREET_CN[snap.street], board: snap.board, result: r, equity: r.equity, exact: true });
        }
      } catch (e) {
        rows.push({ street: STREET_CN[snap.street], board: snap.board, result: null, err: e instanceof Error ? e.message : '计算失败' });
      }
    }
    return rows;
  }, [review, calib, streets, multiway]);

  const myNet = review.net.find((n) => n.seat === review.yourSeat)?.net ?? 0;

  return (
    <div className="mp-review" role="dialog" aria-label="对局复盘">
      <div className="mp-review__panel">
        <header className="mp-review__head">
          <h2>本手复盘 · 第 {review.handNumber} 手</h2>
          <button className="mp-btn mp-btn--ghost mp-btn--tiny" onClick={onClose}>关闭 ✕</button>
        </header>

        {/* 结果概览 */}
        <section className="mp-review__result">
          <div className="mp-review__board">
            {review.board.length > 0
              ? review.board.map((c, i) => <PlayingCard key={i} card={c} size="sm" />)
              : <span className="mp-hint">（本手未发出公共牌）</span>}
          </div>
          <div className={`mp-review__net ${myNet >= 0 ? 'is-up' : 'is-down'}`}>
            本手盈亏：{myNet >= 0 ? '+' : ''}{chips(myNet)}
          </div>
        </section>

        {/* 赢率走势（门控解禁） */}
        <section className="mp-review__equity">
          <h3>你的逐街赢率</h3>
          <p className="mp-review__calib">{calib.text}</p>
          {review.yourHole ? (
            <>
              <div className="mp-review__hole">
                你的底牌：{review.yourHole.map((c, i) => <PlayingCard key={i} card={c} size="sm" />)}
              </div>
              <table className="mp-review__table">
                <thead><tr><th>街</th><th>公共牌</th><th>{calib.mode === 'multiway' ? '多路赢率' : '精确赢率'}</th></tr></thead>
                <tbody>
                  {equityRows.map((row, i) => (
                    <tr key={i}>
                      <td>{row.street}</td>
                      <td>{row.board.length ? row.board.length + ' 张' : '—'}</td>
                      <td className="mp-review__pct">
                        {row.equity != null ? (
                          pct1(row.equity)
                        ) : row.pending ? (
                          <span className="mp-review__pending">计算中…</span>
                        ) : (
                          <span className="mp-review__err">{row.err}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <p className="mp-hint">你本手未参与（未发到底牌），没有可复盘的赢率数据。</p>
          )}
        </section>

        {/* 摊牌亮牌 */}
        <section className="mp-review__showdown">
          <h3>摊牌</h3>
          {review.isUncontested ? (
            <p className="mp-hint">本手未到摊牌（其他玩家均已弃牌），无需亮牌。</p>
          ) : review.reveals && review.reveals.length ? (
            <ul className="mp-review__reveals">
              {review.reveals.map((r) => (
                <li key={r.seat} className={review.winners.includes(r.seat) ? 'is-winner' : ''}>
                  <span className="mp-review__pname">{r.name}{review.winners.includes(r.seat) && ' 🏆'}</span>
                  <span className="mp-review__pcards">{r.hole.map((c, i) => <PlayingCard key={i} card={c} size="sm" />)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="mp-hint">无摊牌信息。</p>}
        </section>

        {review.gameOver && (
          <div className="mp-review__gameover">🏆 本场对局结束，冠军：{review.gameWinnerName ?? '—'}</div>
        )}
      </div>
      <div className="mp-review__scrim" onClick={onClose} />
    </div>
  );
}
